"use client";

import { useState } from "react";
import { AirVent as Lungs, Stethoscope, Clipboard, Moon, Wind, Hospital } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ComprehensiveRespiratoryServices() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = [{
    number: "01",
    title: "Pulmonary Consultations",
    description: "Outpatient pulmonary evaluations for adults with COPD, asthma, chronic cough, shortness of breath, and related respiratory concerns.",
    Icon: Lungs
  }, {
    number: "02",
    title: "COPD & Asthma Care",
    description: "Ongoing pulmonary care for adults managing COPD, asthma, wheezing, breathing changes, and flare-up concerns.",
    Icon: Wind
  }, {
    number: "03",
    title: "Sleep Apnea Evaluation & CPAP Support",
    description: "Evaluation and follow-up care for sleep apnea, CPAP use, mask tolerance, and ongoing sleep-related breathing concerns.",
    Icon: Moon
  }, {
    number: "04",
    title: "Chronic Cough & Shortness of Breath",
    description: "Evaluation of persistent cough, unexplained shortness of breath, and symptoms that may need pulmonary review.",
    Icon: Stethoscope
  }, {
    number: "05",
    title: "Oxygen Therapy Management",
    description: "Pulmonary follow-up for adults who use oxygen therapy or need monitoring related to oxygen needs.",
    Icon: Clipboard
  }, {
    number: "06",
    title: "Post-Hospital Pulmonary Follow-Up",
    description: "Follow-up pulmonary care after hospitalization for respiratory illness, COPD flare-ups, pneumonia, or related breathing concerns.",
    Icon: Hospital
  }];
  return <section data-section-id="2954" id="comprehensive-respiratory-services" className="bg-background text-foreground pt-32 md:pt-44 lg:pt-[180px] pb-24 md:pb-32 px-6 md:px-12 lg:px-20 overflow-hidden">
      <div ref={headerRef} className={`max-w-[1600px] mx-auto transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="flex items-center gap-3 mb-12 md:mb-16">
          <span className="inline-block w-2 h-2 bg-primary" aria-hidden="true" />
          <span className="font-mono text-xs md:text-sm uppercase tracking-[0.15em] text-slate-text">
            / 01 — CARE AREAS
          </span>
        </div>

        <h2 className="font-default font-semibold uppercase leading-[1.1] tracking-tighter text-primary mb-6" style={{
        fontSize: "clamp(2.75rem, 8vw, 5rem)"
      }}>
          PULMONARY CARE & SLEEP MEDICINE
        </h2>
        <p className="text-slate-text text-lg md:text-xl max-w-3xl">
          Chaudhry Pulmonary Associates provides outpatient care for adults with breathing concerns, chronic lung conditions, and sleep-related breathing disorders.
        </p>
      </div>

      <div className="max-w-[1600px] mx-auto mt-24 md:mt-32">
        <div className="h-px w-full bg-border" aria-hidden="true" />

        {services.map((service, i) => <ServiceRow key={service.number} service={service} i={i} />)}
      </div>

      <div className="mt-24 md:mt-32 flex justify-center">
        <Link to="tel:+14126729240">
          <Button size="lg" className="px-12 py-8 text-lg font-mono uppercase tracking-widest rounded-lg">
            CALL (412) 672-9240
          </Button>
        </Link>
      </div>
    </section>;
}
function ServiceRow({
  service,
  i
}: {
  service: {
    number: string;
    title: string;
    description: string;
    Icon: React.ComponentType<{
      className?: string;
      strokeWidth?: number;
    }>;
  };
  i: number;
}) {
  const [hovered, setHovered] = useState(false);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    Icon
  } = service;
  return <div data-index={i} ref={ref} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} className={`group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 py-12 md:py-16 lg:py-20 border-b border-border transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className="lg:col-span-1 flex lg:block items-baseline gap-4">
        <div className="flex flex-col">
          <span className={`font-serif font-medium leading-none transition-colors duration-500 ${hovered ? "text-primary" : "text-slate-text"}`} style={{
          fontSize: "clamp(3.5rem, 6vw, 6rem)"
        }}>
            {service.number}
          </span>
          <span className={`block mt-3 h-[2px] bg-primary transition-all duration-500 ${hovered ? "w-20" : "w-10"}`} aria-hidden="true" />
        </div>
      </div>

      <div className="lg:col-span-7">
        <h3 className="font-default font-semibold uppercase tracking-tight text-2xl md:text-3xl lg:text-[36px] leading-[1.1] mb-5 md:mb-6 text-foreground">
          {service.title}
        </h3>
        <p className="font-default text-base md:text-lg text-slate-text" style={{
        lineHeight: 1.6,
        maxWidth: "60ch"
      }}>
          {service.description}
        </p>
      </div>

      <div className="lg:col-span-4 flex flex-row lg:flex-col items-start lg:items-end justify-between lg:justify-start gap-6 lg:gap-8">
        <div className="transition-transform duration-500 ease-out" style={{
        transform: hovered ? "scale(1.05)" : "scale(1)"
      }}>
          <div className="p-4 rounded-lg bg-secondary">
            <Icon className="w-12 h-12 md:w-16 md:h-16 text-primary" strokeWidth={1.5} />
          </div>
        </div>

        <div className="hidden lg:block" />
      </div>
    </div>;
}